import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAnimation } from 'framer-motion';
import { Container, StyledTerm, HoverWrapper, Definition, Arrow } from './styled';

// this is how close to the edge the definition tooltip is allowed
// to go, in px
const X_BOUND = 20;

const TermDefinition = ({ term, children }) => {
  const defRef = useRef();
  const controls = useAnimation();

  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isHovering || !defRef.current) {
      return;
    }
    defRef.current.style.transform = '';
    const defRect = defRef.current.getBoundingClientRect();

    // if element with this data-id exists, use it so that the
    // definition box never renders outside of it
    const containerParent = defRef.current.closest(
      '[data-id="term-definition-container"]'
    );

    if (containerParent) {
      const containerRect = containerParent.getBoundingClientRect();

      if (defRect.left < X_BOUND + containerRect.left) {
        defRef.current.style.transform = `translateX(${
          X_BOUND + containerRect.left - defRect.left
        }px)`;
      }
    } else if (defRect.left < X_BOUND) {
      defRef.current.style.transform = `translateX(${X_BOUND - defRect.left}px)`;
    }

    if (defRect.left + defRect.width > window.innerWidth - X_BOUND) {
      defRef.current.style.transform = `translateX(-${
        defRect.left + defRect.width - window.innerWidth + X_BOUND
      }px)`;
    }

    setIsVisible(true);
  }, [isHovering]);

  useEffect(() => {
    if (isVisible) {
      controls.start({ opacity: 1 });
      return;
    }
    controls.start({ opacity: 0 });
  }, [isVisible, controls]);

  return (
    <Container
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsVisible(false);
        setTimeout(() => {
          setIsHovering(false);
        }, 200);
      }}
    >
      <StyledTerm>{term}</StyledTerm>
      {isHovering ? (
        <HoverWrapper animate={controls} transition={{ duration: 0.2 }}>
          <Arrow />
          <Definition ref={defRef}>{children}</Definition>
        </HoverWrapper>
      ) : (
        // hack to prevent nudging periods/commas a tiny bit
        // when definition is shown
        <div style={{ position: 'absolute' }} />
      )}
    </Container>
  );
};

TermDefinition.propTypes = {
  children: PropTypes.node,
  term: PropTypes.string.isRequired
};

export default TermDefinition;
